/**
 * Search Section Component
 * Displays filtered games based on search query
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LAST_PLAYED_SECTION_NAME, SECTION_NAMES } from '@repo/common';
import { GRID_GAP, INGAME_OPEN_TYPE } from '../../../../constants';
import { useOpenType } from '../../../../core/providers/OpenTypeContext';
import { clickCounter } from '../../../../core/services/EventTracker';
import { sessionService } from '../../../../core/services/SessionService';
import { testIds } from '../../../../core/testIds';
import { useMeasuredColumnCount } from '../../../../shared/hooks/useMeasuredColumnCount';
import { useAppStore, useSections } from '../../../../store/app/appStore';
import { useCampaigns, PRIZE_POP_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from '../../../campaigns';
import Game from '../../../games/components/GameCard';
import { SEARCH_CAMPAIGNS_OPTIONS } from '../../constants/searchConstants';
import { useFilterConfig } from '../../hooks/useFilterConfig';
import { useFilterPanel } from '../../hooks/useFilterPanel';
import { filterGames } from '../../utils/gameFilterHelpers';
import { FilterPanel } from '../FilterPanel';
import { RandomGameModal } from '../RandomGameModal';
import styles from './SearchSection.module.css';

const WIDE_MIN_CARD_WIDTH = 220;
const INGAME_CONTAINER_INSET = 2;

// Matches the breakpoints .gamesGrid used before it switched to a measured --columns
// (SearchSection.module.css): 2 / 3 / 4 / 5 / 6, then a dynamic count above 1600px that
// keeps cards at least WIDE_MIN_CARD_WIDTH wide, mirroring the auto-fill/minmax it replaced.
const getSearchColumnCount = (width: number): number => {
  if (width >= 1600) {
    return Math.max(6, Math.floor((width + GRID_GAP.desktop) / (WIDE_MIN_CARD_WIDTH + GRID_GAP.desktop)));
  }
  if (width >= 1280) return 6;
  if (width >= 1024) return 5;
  if (width >= 576) return 4;
  if (width >= 389) return 3;
  return 2;
};

const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path
      d="M11 1L1.45406 10.5459C1.2588 10.7412 1.2588 11.0578 1.45406 11.253L11 20.799"
      stroke="#F9F9F9"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect x="1" y="10" width="21" height="2" rx="1" fill="#F9F9F9" />
  </svg>
);

const ClearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const ShuffleIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
    <path
      d="M5.55822 4.7788H0.796458C0.356614 4.7788 0 4.42219 0 3.98234C0 3.5425 0.356614 3.18588 0.796458 3.18588H5.55822C7.35443 3.18588 9.05089 4.0586 10.095 5.5205L11.0211 6.81694L10.0423 8.18724L8.79881 6.44638C8.05313 5.40223 6.84171 4.7788 5.55822 4.7788ZM23.7666 14.5697L20.5808 11.3838C20.2695 11.0726 19.7656 11.0726 19.4546 11.3838C19.1435 11.695 19.1433 12.199 19.4546 12.51L21.2808 14.3363H18.4417C17.1584 14.3363 15.947 13.7129 15.2011 12.6687L13.9577 10.9279L12.9788 12.2982L13.9049 13.5946C14.9491 15.0565 16.6453 15.9292 18.4417 15.9292H21.2808L19.4546 17.7555C19.1433 18.0667 19.1433 18.5707 19.4546 18.8817C19.6101 19.0372 19.814 19.1151 20.0177 19.1151C20.2214 19.1151 20.4252 19.0372 20.5808 18.8817L23.7666 15.6959C24.0778 15.3848 24.0778 14.8807 23.7666 14.5697ZM23.7666 3.41925L20.5808 0.233412C20.2695 -0.077804 19.7656 -0.077804 19.4546 0.233412C19.1435 0.544628 19.1433 1.04859 19.4546 1.3596L21.2808 3.18588H18.4417C16.6455 3.18588 14.9491 4.0586 13.9045 5.5209L8.79941 12.6683C8.05312 13.7129 6.84171 14.3363 5.55822 14.3363H0.796458C0.356614 14.3363 0 14.6929 0 15.1328C0 15.5726 0.356614 15.9292 0.796458 15.9292H5.55822C7.35443 15.9292 9.05089 15.0565 10.0954 13.5942L15.2005 6.44678C15.9468 5.40223 17.1582 4.7788 18.4417 4.7788H21.2808L19.4546 6.60508C19.1433 6.9163 19.1433 7.42025 19.4546 7.73127C19.6101 7.88678 19.814 7.96463 20.0177 7.96463C20.2214 7.96463 20.4252 7.88678 20.5808 7.73127L23.7666 4.54544C24.0778 4.23442 24.0778 3.73026 23.7666 3.41925Z"
      style={{ fill: isActive ? 'var(--piliot-green, #3CFF00)' : '#FFFFFF' }}
    />
  </svg>
);

const FilterIcon = ({ hasActiveFilters }: { hasActiveFilters?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M8.09961 11.6748C8.09961 11.5095 8.04026 11.3491 7.93262 11.2236L1.06445 3.22754C0.880599 3.01291 0.756045 2.74184 0.710938 2.44629C0.665849 2.15065 0.703152 1.8483 0.816406 1.57715C0.929682 1.3061 1.11259 1.08151 1.33691 0.926758C1.50487 0.810914 1.69143 0.736738 1.88379 0.707031L2.07715 0.692383H17.9229L18.1162 0.707031C18.3086 0.736738 18.4951 0.810914 18.6631 0.926758C18.8874 1.08151 19.0703 1.3061 19.1836 1.57715C19.2968 1.8483 19.3342 2.15065 19.2891 2.44629C19.2496 2.70505 19.1488 2.94479 19.001 3.14453L18.9355 3.22754L12.0674 11.2236C11.9597 11.3491 11.9004 11.5095 11.9004 11.6748V16.0781L11.8896 16.1318L11.8672 16.1621L9.55273 18.123L8.16309 19.2988V19.2998C8.15808 19.3041 8.15362 19.3058 8.15137 19.3066C8.14939 19.3074 8.14819 19.3076 8.14746 19.3076H8.14551L8.14062 19.3057L8.11621 19.2793C8.10659 19.2631 8.0997 19.2409 8.09961 19.2158V11.6748Z"
      style={{ stroke: hasActiveFilters ? 'var(--piliot-green, #3CFF00)' : 'white' }}
      strokeWidth="1.38462"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

export const SearchSection: React.FC = () => {
  const { t } = useTranslation();
  const isSearchActive = useAppStore((state) => state.isSearchActive);
  const setSearchActive = useAppStore((state) => state.setSearchActive);
  const { sections } = useSections();
  const { openType } = useOpenType();
  const filterApi = useFilterPanel();
  const { handleClearFilters, resetAccordion, selectedFilters } = filterApi;
  const tournaments = useCampaigns(TOURNAMENTS_CAMPAIGN_TYPE);
  const prizePopCampaigns = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE);

  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isRandomModalOpen, setIsRandomModalOpen] = useState(false);
  const lastTrackedQuery = useRef<string>('');
  const isIngame = openType === INGAME_OPEN_TYPE;
  const measureSearchColumnCount = useCallback(
    (width: number) => getSearchColumnCount(isIngame ? width + INGAME_CONTAINER_INSET : width),
    [isIngame],
  );
  const [columnCount, resultsContainerRef] = useMeasuredColumnCount(measureSearchColumnCount, 2);

  // Count total selected filters
  const totalSelectedFilters = useMemo(() => {
    return Object.values(selectedFilters).reduce((total, filterSet) => total + filterSet.size, 0);
  }, [selectedFilters]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Track search queries when debounced query changes
  useEffect(() => {
    // Only track if we have a meaningful query (at least 2 characters)
    // and it's different from the last tracked query
    if (debouncedQuery.length >= 2 && debouncedQuery !== lastTrackedQuery.current) {
      // Track in SessionService for Sentry (as array)
      sessionService.trackSearchQuery(debouncedQuery);

      // Track in event order only (not as a counted interaction)
      clickCounter.trackEventOrderOnly(`search_query_${debouncedQuery.toLowerCase().replace(/\s+/g, '_')}`);

      lastTrackedQuery.current = debouncedQuery;
    }
  }, [debouncedQuery]);

  // Clear input when search is closed
  useEffect(() => {
    if (!isSearchActive) {
      setInputValue('');
      setDebouncedQuery('');
      setIsFilterOpen(false);
      setIsRandomModalOpen(false);
      handleClearFilters();
      // Reset last tracked query when search closes
      lastTrackedQuery.current = '';
    }
  }, [isSearchActive, handleClearFilters]);

  // Reset accordion state when filter panel closes
  useEffect(() => {
    if (!isFilterOpen) {
      resetAccordion();
    }
  }, [isFilterOpen, resetAccordion]);

  // Get Last Played games to show as default when search is empty
  const lastPlayedGames = useMemo(() => {
    const lastPlayedSection = sections.find((s) => s.name === LAST_PLAYED_SECTION_NAME);
    return lastPlayedSection?.games || [];
  }, [sections]);

  // Get all unique games (shared computation)
  // const allUniqueGames = useMemo(() => {
  //   const allGames = sections.flatMap(section => section.games);
  //   return Array.from(
  //     new Map(allGames.map(game => [game.gameKey, game])).values()
  //   );
  // }, [sections]);

  const allUniqueGames = useMemo(() => {
    const allGames = sections.find((section) => section.name === SECTION_NAMES.ALL_GAMES);
    return allGames?.games || [];
  }, [sections]);

  // Total games count for placeholder
  const totalGamesCount = allUniqueGames.length;

  // Get filter configuration with dynamic options based on available games
  const filters = useFilterConfig(allUniqueGames);

  // Collect all game keys for active campaigns to optimize campaign filtering
  const campaignGameKeysByType = useMemo(() => {
    // Tournaments are already pre-filtered during parsing in the store to include only existing lobby games (parseTournamentGames)
    const tournamentGameKeys = new Set<string>(
      tournaments.flatMap((tournament) =>
        tournament.games.map((game) => (typeof game === 'string' ? game : game.gameKey)),
      ),
    );

    // Prize Pop returns raw game keys, so we filter to only include games that are valid (same as tournaments parsing filtration)
    const allUniqueGamesKeys = new Set(allUniqueGames.map((game) => game.gameKey));
    const prizePopGameKeys = new Set<string>(
      prizePopCampaigns
        .flatMap((campaign) => campaign.games.map((game) => (typeof game === 'string' ? game : game.gameKey)))
        .filter((key) => allUniqueGamesKeys.has(key))
    );

    return {
      [SEARCH_CAMPAIGNS_OPTIONS.TOURNAMENTS]: tournamentGameKeys,
      [SEARCH_CAMPAIGNS_OPTIONS.PRIZE_POP]: prizePopGameKeys,
    };
  }, [tournaments, prizePopCampaigns, allUniqueGames]);

  // Filter games based on debounced search query and selected filters
  const filteredGames = useMemo(() => {
    const query = debouncedQuery.toLowerCase();
    const filteredUniqueGames = query
      ? allUniqueGames.filter((game) => game.name.toLowerCase().includes(query))
      : allUniqueGames;

    if (totalSelectedFilters > 0) {
      return filterGames(filteredUniqueGames, selectedFilters, campaignGameKeysByType);
    }

    return filteredUniqueGames;
  }, [allUniqueGames, campaignGameKeysByType, debouncedQuery, selectedFilters, totalSelectedFilters]);

  // Open random game modal
  const openRandomModal = useCallback(() => {
    if (allUniqueGames.length > 0) {
      setIsRandomModalOpen(true);
    }
  }, [allUniqueGames.length]);

  if (!isSearchActive) return null;

  return (
    <div className={`${styles.searchContainer} ${openType === INGAME_OPEN_TYPE ? styles.ingame : ''}`}>
      <div className={styles.searchHeader}>
        <button
          onClick={() => setSearchActive(false)}
          className={styles.backButton}
          data-testid={testIds.search.backButton}
        >
          <BackArrowIcon />
        </button>

        <div className={styles.searchInputWrapper}>
          <input
            id="search-input"
            type="search"
            placeholder={t('search.placeholder', `Search among ${totalGamesCount} games`, { count: totalGamesCount })}
            value={inputValue}
            maxLength={50}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsFilterOpen(false)}
            autoFocus
            className={styles.searchInput}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
          />
          {inputValue.length > 0 && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInputValue('');
                setDebouncedQuery('');
                setIsFilterOpen(false);
              }}
              className={styles.clearButton}
            >
              <ClearIcon />
            </button>
          )}
        </div>

        <button
          className={`${styles.headerActionButton} ${isRandomModalOpen && styles.headerActionButtonActive}`}
          onClick={openRandomModal}
          data-testid={testIds.search.randomizerButton(isRandomModalOpen ? 'open' : 'closed')}
        >
          <ShuffleIcon isActive={isRandomModalOpen} />
        </button>
        <button
          className={`${styles.headerActionButton} ${isFilterOpen && styles.headerActionButtonActive}`}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          data-testid={testIds.search.filterButton(isFilterOpen ? 'open' : 'closed')}
        >
          <FilterIcon hasActiveFilters={totalSelectedFilters > 0} />
          {totalSelectedFilters > 0 && <span className={styles.filterBadge}>{totalSelectedFilters}</span>}
        </button>
      </div>

      {isFilterOpen && (
        <FilterPanel
          filterApi={filterApi}
          filteredGamesCount={inputValue.length > 0 || totalSelectedFilters > 0 ? filteredGames.length : null}
          filters={filters}
          onClose={() => setIsFilterOpen(false)}
        />
      )}

      <div
        className={styles.resultsContainer}
        ref={resultsContainerRef}
        style={{ '--columns': columnCount } as React.CSSProperties}
      >
        {!debouncedQuery.trim() && !totalSelectedFilters ? (
          // Show Last Played games as default, or placeholder if none
          lastPlayedGames.length > 0 ? (
            <>
              <div className={styles.searchSectionHeader}>{t('sections.names.LAST_PLAYED', 'Last Played')}</div>
              <div className={styles.gamesGrid}>
                {lastPlayedGames.map((game, index) => (
                  <Game key={`${game.gameKey}_${index}`} data={game} sectionName="search" viewMode="single" />
                ))}
              </div>
            </>
          ) : (
            <div className={styles.searchPlaceholder}>
              <SearchIcon />
              {t('search.search_no_entry', 'Please enter a search word')}
            </div>
          )
        ) : filteredGames.length > 0 ? (
          <>
            <div className={styles.searchSectionHeader}>
              {t(
                filteredGames.length === 1 ? 'search.game_found' : 'search.games_found',
                `${filteredGames.length} Games Found`,
                { count: filteredGames.length },
              )}
            </div>
            <div className={styles.gamesGrid}>
              {filteredGames.map((game, index) => (
                <Game key={`${game.gameKey}_${index}`} data={game} sectionName="search" viewMode="single" />
              ))}
            </div>
          </>
        ) : inputValue.length > 0 ? (
          <div className={styles.noResultsMessage}>
            {t('search.no_search_results', `No results found for "${debouncedQuery}"`, { term: `"${debouncedQuery}"` })}
          </div>
        ) : (
          <div className={styles.noResultsMessage}>{t('search.no_filter_results', 'No results found')}</div>
        )}
      </div>

      {isRandomModalOpen && <RandomGameModal allGames={allUniqueGames} onClose={() => setIsRandomModalOpen(false)} />}
    </div>
  );
};
